

import { IEntityHandler, MigrationOperation, MigrationResponse } from '../interfaces';
import { MigrationClient } from '../MigrationClient';
import { TemplateTransformer } from '../transformers/TemplateTransformer';

export class TemplateHandler implements IEntityHandler {
  private migrationClient = MigrationClient.getInstance();
  private transformer = new TemplateTransformer();

  async handleOperation(
    operation: MigrationOperation,
    amplifyData: Record<string, unknown>,
    amplifyResult?: Record<string, unknown>
  ): Promise<MigrationResponse> {
    const templateData = (amplifyResult as Record<string, Record<string, unknown>>)?.data?.createTemplate || amplifyData;

    try {
      // First, create/update the main template record
      const transformedData = await this.transformer.transform(templateData as Record<string, unknown>);
      
      // Check if transformation decided to skip
      if ((transformedData as Record<string, unknown>).skipped) {
        return {
          success: true,
          operation,
          entity_type: 'Template',
          skipped: true,
          reason: (transformedData as Record<string, unknown>).reason as string,
          timestamp: new Date().toISOString()
        };
      }

      const templateResult = await this.migrationClient.invoke(operation, 'Template', transformedData);
      
      if (!templateResult.success) {
        return templateResult;
      }

      // If creating a template, also create its nested sections, subsections, and fields
      if (operation === 'CREATE' && (templateData as TemplateWithSections).sections) {
        await this.createTemplateSections(templateData as TemplateWithSections);
      }

      return templateResult;
    } catch (error) {
      console.error(`[TEMPLATE-HANDLER] Template operation failed:`, error);
      return {
        success: false,
        operation,
        entity_type: 'Template',
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Handle template section creation specifically
  async handleTemplateSectionOperation(
    operation: MigrationOperation,
    sectionData: { template_id: string; name: string; content?: string; order_index?: number },
  ): Promise<MigrationResponse> {
    const payload = {
      template_id: sectionData.template_id,
      name: sectionData.name,
      content: sectionData.content ?? '',
      order_index: typeof sectionData.order_index === 'number' ? sectionData.order_index : 0,
      created_by: null, // Will be auto-populated by MigrationClient
      owners: [], // Will be auto-populated by MigrationClient
    };
    return await this.migrationClient.invoke(operation, 'TemplateSection', payload);
  }

  // Create template sections and their nested structure
  private async createTemplateSections(templateData: TemplateWithSections): Promise<void> {
    if (!templateData.sections?.items) return;

    console.log(`[TEMPLATE-HANDLER] Creating ${templateData.sections.items.length} sections for template ${templateData.id}`);
    
    for (const section of templateData.sections.items) {
      try {

        let sectionContent = '';
        if (section.textboxes?.items) {
          const textboxValues = section.textboxes.items
            .filter((textbox: TextboxItem) => textbox?.value)
            .map((textbox: TextboxItem) => textbox.value)
            .join('\n\n');
          sectionContent = textboxValues;
          console.log(`[TEMPLATE-HANDLER] Section ${section.name} has ${section.textboxes.items.length} textboxes, content length: ${sectionContent.length}`);
        } else {
          console.log(`[TEMPLATE-HANDLER] Section ${section.name} has no textboxes`);
        }

        // Create template section with textbox values as content
        const sectionPayload = {
          id: section.id,
          name: section.name,
          template_id: templateData.id,
          content: sectionContent, // Textbox.value -> Section.content
          order_index: section.order || 0,
          created_at: section.createdAt,
          updated_at: section.updatedAt,
        };

        await this.migrationClient.invoke('CREATE', 'TemplateSection', sectionPayload);
        console.log(`[TEMPLATE-HANDLER] Successfully created section: ${section.name}`);

        if (section.subsections?.items) {
          console.log(`[TEMPLATE-HANDLER] Creating ${section.subsections.items.length} subsections for section ${section.name}`);
          await this.createTemplateSubsections(section.subsections.items, section.id, templateData.id);
        }


      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.warn(`[TEMPLATE-HANDLER] Failed to create section ${section.name}: ${errorMessage}`);
      }
    }
  }

  private async createTemplateSubsections(subsections: SubsectionItem[], sectionId: string, templateId: string): Promise<void> {
    for (const subsection of subsections) {
      try {
        const subsectionPayload = {
          id: subsection.id,
          name: subsection.name,
          section_id: sectionId,
          template_id: templateId,
          field_order: subsection.fieldOrder || [],
          created_at: subsection.createdAt,
          updated_at: subsection.updatedAt,
          owners: subsection.owners || [],
          version: subsection._version || 1
        };

        await this.migrationClient.invoke('CREATE', 'TemplateSubsection', subsectionPayload);

        // Create fields if they exist
        if (subsection.fields?.items) {
          await this.createTemplateFields(subsection.fields.items, subsection.id, sectionId, templateId);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.warn(`[TEMPLATE-HANDLER] Failed to create subsection ${subsection.name}: ${errorMessage}`);

      }
    }
  }

  private async createTemplateFields(fields: FieldItem[], subsectionId: string, sectionId: string, templateId: string): Promise<void> {
    for (const field of fields) {
      try {
        const fieldPayload = {
          id: field.id,
          name: field.name,
          field_type: field.fieldType || 'text',
          subsection_id: subsectionId,
          section_id: sectionId,
          template_id: templateId,
          placeholder: field.placeholder || '',
          is_required: field.isRequired || false,
          options: field.options || [],
          created_at: field.createdAt,
          updated_at: field.updatedAt,
          owners: field.owners || [],
          version: field._version || 1
        };

        await this.migrationClient.invoke('CREATE', 'TemplateField', fieldPayload);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.warn(`[TEMPLATE-HANDLER] Failed to create field ${field.name}: ${errorMessage}`);
      }
    }
  }
}


interface TextboxItem {
  id: string;
  value: string;
  name?: string;
}

interface FieldItem {
  id: string;
  name: string;
  fieldType?: string;
  placeholder?: string;
  isRequired?: boolean;
  options?: string[];
  createdAt?: string;
  updatedAt?: string;
  owners?: string[];
  _version?: number;
}

interface SubsectionItem {
  id: string;
  name: string;
  fieldOrder?: string[];
  createdAt?: string;
  updatedAt?: string;
  owners?: string[];
  _version?: number;
  fields?: {
    items: FieldItem[];
  };
}

interface SectionItem {
  id: string;
  name: string;
  order?: number;
  createdAt?: string;
  updatedAt?: string;
  textboxes?: {
    items: TextboxItem[];
  };
  subsections?: {
    items: SubsectionItem[];
  };
}

interface TemplateWithSections {
  id: string;
  sections: {
    items: SectionItem[];
  };
} 